import {
  DirectionsRenderer,
  GoogleMap,
  LoadScript,
  Marker,
} from "@react-google-maps/api";
import { TimePicker } from "../../../../components/inputs/MomentDatePicker";
import { DeleteOutlined, HolderOutlined } from "@ant-design/icons";
import { Button, Divider, Form, Input, message, Modal, Popconfirm, Table, Typography } from "antd";
import axios from "axios";
import moment from "moment";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import MultipleUpload from "../../../../components/inputs/UploadMultipleFile";
import { serverUrl } from "../../../../nestserver";
const COORDINATE_PATTERN = /^-?\d*\.?\d*$/;

const toCoordinate = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const hasCoordinates = (field) =>
  toCoordinate(field?.coordinates?.[0]) !== null &&
  toCoordinate(field?.coordinates?.[1]) !== null;

// Defined once: @react-google-maps/api compares these by identity, and a new
// object on every render re-applies them to the map, which drops the markers.
const PREVIEW_MAP_CONTAINER_STYLE = {
  width: "100%",
  height: "240px",
  borderRadius: "8px",
  border: "1px solid #e5e7eb",
};
const PREVIEW_MAP_OPTIONS = { disableDefaultUI: true, zoomControl: true };
const PREVIEW_MAP_FALLBACK_CENTER = { lat: 28.7041, lng: 77.1025 };

const AddEditRouteLive = () => {
  const { Title } = Typography;
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const format = "HH:mm";

  const [applyLeavePopup, setApplyLeavePopup] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editData, setEditData] = useState(null);
  const [uploadAdmissionPopup, setUploadAdmissionPopup] = useState(false);
  const [parentFileList, setParentFileList] = useState([]);
  const [otherFields, setOtherFields] = useState([
    { name: "", coordinates: null, time: null },
  ]);

  const [allFields, setAllFields] = useState([
    { name: "", coordinates: null, time: null },
  ]);

  const [feeStops, setFeeStops] = useState([]);
  const [newFeeStopName, setNewFeeStopName] = useState("");
  const [editingFeeStop, setEditingFeeStop] = useState(null);
  const [editingFeeStopName, setEditingFeeStopName] = useState("");

  const previewMapRef = useRef(null);
  const [previewMapReady, setPreviewMapReady] = useState(false);
  const [previewDirections, setPreviewDirections] = useState(null);

  // Row being dragged, and the row it is currently hovering over.
  const [dragIndex, setDragIndex] = useState(null);
  const [dropIndex, setDropIndex] = useState(null);

  const handlePreviewMapLoad = useCallback((map) => {
    previewMapRef.current = map;
    // The map carries no center/zoom props, so give it a starting view. The
    // framing effect then moves it onto the points, including when a saved
    // route was already loaded before the map existed.
    map.setCenter(PREVIEW_MAP_FALLBACK_CENTER);
    map.setZoom(13);
    setPreviewMapReady(true);
  }, []);

  // Frame the map around the points whenever they change. This is the only
  // thing that moves the camera — the map has no controlled center/zoom, which
  // would otherwise snap straight back to the first point and hide the rest.
  useEffect(() => {
    if (!previewMapRef.current || typeof window.google === "undefined") return;
    const validFields = otherFields.filter(hasCoordinates);
    if (validFields.length === 0) return;

    const positions = validFields.map((f) => ({
      lat: toCoordinate(f.coordinates[0]),
      lng: toCoordinate(f.coordinates[1]),
    }));

    if (positions.length === 1) {
      previewMapRef.current.setCenter(positions[0]);
      previewMapRef.current.setZoom(15);
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    positions.forEach((position) => bounds.extend(position));
    previewMapRef.current.fitBounds(bounds, 40);
    // previewMapReady is a dependency so this also runs once the map exists,
    // for a saved route whose points were loaded before it.
  }, [otherFields, previewMapReady]);

  // Compute road-following directions for preview map
  useEffect(() => {
    if (typeof window.google === "undefined") return;
    setPreviewDirections(null);

    const validFields = otherFields.filter(hasCoordinates);
    if (validFields.length < 2) return;

    const toLatLng = (f) =>
      new window.google.maps.LatLng(
        toCoordinate(f.coordinates[0]),
        toCoordinate(f.coordinates[1]),
      );

    const origin = toLatLng(validFields[0]);
    const destination = toLatLng(validFields[validFields.length - 1]);
    const waypoints = validFields.slice(1, -1).map((f) => ({
      location: toLatLng(f),
      stopover: true,
    }));

    new window.google.maps.DirectionsService().route(
      {
        origin,
        destination,
        waypoints,
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === "OK") setPreviewDirections(result);
      },
    );
  }, [otherFields]);

  const handleOtherFieldChange = (value, index, field) => {
    const newFields = [...otherFields];

    if (field === "time" && value) {
      const timeInFormat = moment(value).format("HH:mm");
      newFields[index][field] = timeInFormat;
    } else {
      newFields[index][field] = value;
    }

    setOtherFields(newFields);
    setAllFields(newFields);
  };

  const setPointCoordinates = (index, coordinates) => {
    const newFields = otherFields.map((field, i) =>
      i === index ? { ...field, coordinates } : field,
    );
    setOtherFields(newFields);
    setAllFields(newFields);
  };

  const moveField = (from, to) => {
    if (from == null || to == null || from === to) return;

    const reordered = [...otherFields];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);

    setOtherFields(reordered);
    setAllFields(reordered);
  };

  // axis: 0 = latitude, 1 = longitude
  const handleCoordinateChange = (rawValue, index, axis) => {
    if (!COORDINATE_PATTERN.test(rawValue)) return;
    const coordinates = [...(otherFields[index]?.coordinates ?? ["", ""])];
    coordinates[axis] = rawValue;
    setPointCoordinates(index, coordinates);
  };

  const addOtherField = () => {
    if (Array.isArray(otherFields)) {
      setOtherFields([
        ...otherFields,
        { name: "", time: null, coordinates: null },
      ]);
    } else {
      setOtherFields([{ name: "", time: null, coordinates: null }]);
    }
  };

  const removeOtherField = (index) => {
    if (index !== undefined && index >= 0) {
      const newFieldss = otherFields.filter((_, i) => i !== index);
      setOtherFields(newFieldss);
      const newFields = allFields.reduce((acc, field, i) => {
        if (i === index) {
          if (field.route_id) {
            acc.push({ ...field, is_deleted: true });
          }
        } else {
          acc.push(field);
        }
        return acc;
      }, []);
      setAllFields(newFields);
    }
  };

  async function getFormList() {
    const res = await axios({
      method: "get",
      url: serverUrl + "/routes/v2/",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data;
  }
  const formList = useQuery("/routes/v2/", getFormList);

  const handleFileListChange = (fileList) => {
    setParentFileList(fileList);
  };
  async function fetchSubjects(id) {
    const res = await axios({
      method: "get",
      url: `${serverUrl}/routes/v2/${id}`,
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });

    return res.data;
  }
  const busDetails = useQuery(
    ["routes/v2", editData],
    () => editData && fetchSubjects(editData),
    {
      enabled: !!editData,
    },
  );

  const feeStopsQuery = useQuery(
    ["route-fee-stops", editData],
    () =>
      axios
        .get(`${serverUrl}/admin/route-fee-stop?route_id=${editData}`, {
          headers: { Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}` },
        })
        .then((r) => r.data?.result || []),
    {
      enabled: !!editData && isEditMode,
      onSuccess: (data) => setFeeStops(data),
    }
  );

  const authHeader = () => ({
    Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
  });

  const handleAddFeeStop = () => {
    if (!newFeeStopName.trim()) return;
    axios
      .post(
        `${serverUrl}/admin/route-fee-stop`,
        { name: newFeeStopName.trim(), route_id: editData, order: feeStops.length },
        { headers: authHeader() }
      )
      .then((res) => {
        setFeeStops((prev) => [...prev, res.data?.result]);
        setNewFeeStopName("");
        queryClient.invalidateQueries(["route-fee-stops", editData]);
      })
      .catch(() => message.error("Failed to add fee stop"));
  };

  const handleUpdateFeeStop = (id) => {
    if (!editingFeeStopName.trim()) return;
    axios
      .patch(
        `${serverUrl}/admin/route-fee-stop/${id}`,
        { name: editingFeeStopName.trim() },
        { headers: authHeader() }
      )
      .then(() => {
        setFeeStops((prev) =>
          prev.map((s) => (s._id === id ? { ...s, name: editingFeeStopName.trim() } : s))
        );
        setEditingFeeStop(null);
        setEditingFeeStopName("");
      })
      .catch(() => message.error("Failed to update fee stop"));
  };

  const handleDeleteFeeStop = (id) => {
    axios
      .delete(`${serverUrl}/admin/route-fee-stop/${id}`, { headers: authHeader() })
      .then(() => {
        setFeeStops((prev) => prev.filter((s) => s._id !== id));
        queryClient.invalidateQueries(["route-fee-stops", editData]);
      })
      .catch(() => message.error("Failed to delete fee stop"));
  };

  const columns = [
    {
      title: "Name",
      dataIndex: [],
      key: "",
      align: "center",
      render: (record) => (
        <div
          key={record}
          style={{ color: "#111111" }}
          className="text-base text-center capitalize"
        >
          {`${record?.name}`}
        </div>
      ),
    },
    {
      title: "Source",
      dataIndex: [],
      key: "",
      align: "center",
      render: (record) => (
        <div
          key={record}
          style={{ color: "#111111" }}
          className="text-base text-center capitalize"
        >
          {record?.start_point?.name || record?.source || "---"}
        </div>
      ),
    },
    {
      title: "Destination",
      dataIndex: [],
      key: "",
      align: "center",
      render: (record) => (
        <div
          key={record}
          style={{ color: "#111111" }}
          className="text-base text-center capitalize"
        >
          {record?.end_point?.name || record?.destination || "---"}
        </div>
      ),
    },
    {
      title: "Action",
      dataIndex: [],
      key: "",
      align: "center",
      render: (record, index) => (
        <div
          key={record?._id}
          style={{ color: "#111111" }}
          className=" text-base font-semibold text-center cursor-pointer text-blue-500 "
          onClick={() => {
            setIsEditMode(true);
            setEditData(record?._id);
            setApplyLeavePopup(true);
          }}
        >
          <span className="text-blue-500">Edit</span>
        </div>
      ),
    },
  ];

  const onFinish = (values) => {
    if (otherFields.length < 2) {
      return message.error("Please add at least Start and End points");
    }

    const missingCoordinates = otherFields.find(
      (f) =>
        !hasCoordinates(f) ||
        (toCoordinate(f.coordinates[0]) === 0 &&
          toCoordinate(f.coordinates[1]) === 0),
    );
    if (missingCoordinates) {
      return message.error(
        `Please enter a valid latitude and longitude for "${
          missingCoordinates.name || "one of the points"
        }"`,
      );
    }

    const startPoint = {
      name: otherFields[0].name,
      latitude: toCoordinate(otherFields[0].coordinates[0]),
      longitude: toCoordinate(otherFields[0].coordinates[1]),
      estimated_time: otherFields[0].time,
    };

    const endPoint = {
      name: otherFields[otherFields.length - 1].name,
      latitude: toCoordinate(
        otherFields[otherFields.length - 1].coordinates[0],
      ),
      longitude: toCoordinate(
        otherFields[otherFields.length - 1].coordinates[1],
      ),
      estimated_time: otherFields[otherFields.length - 1].time,
    };

    const pickupPoints = otherFields.slice(1, -1).map((field, index) => ({
      name: field.name,
      latitude: toCoordinate(field.coordinates[0]),
      longitude: toCoordinate(field.coordinates[1]),
      estimated_time: field.time,
      order: index + 1,
    }));

    const payload = {
      name: values?.name,
      comments: values?.comments,
      start_point: startPoint,
      end_point: endPoint,
      pickup_points: pickupPoints,
    };

    if (payload) {
      if (isEditMode) {
        axios
          .put(
            `${serverUrl}/routes/v2/${editData ? editData : null}`,
            payload,
            {
              headers: {
                Authorization: `Bearer ${window.localStorage.getItem(
                  "jwt-token",
                )}`,
              },
            },
          )
          .then((res) => {
            queryClient.invalidateQueries("/routes/v2/");
            form.resetFields();
            setApplyLeavePopup(false);
            setEditData(null);
            setIsEditMode(false);
            message.success(res?.data?.result?.message);
          })
          .catch((err) => {
            if (err?.response?.data?.error?.errors) {
              message.error(err?.response?.data?.error?.errors);
            }
          });
      } else {
        axios({
          method: "post",
          url: serverUrl + "/routes/v2/",
          data: payload,
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        })
          .then((res) => {
            queryClient.invalidateQueries("/routes/v2/");
            form.resetFields();
            setApplyLeavePopup(false);
            message.success(res?.data?.result?.message);
          })
          .catch((error) => {
            message.error(error?.response?.data?.error?.errors);
          });
      }
    }
  };

  useEffect(() => {
    if (isEditMode && busDetails.data) {
      const data = busDetails.data?.result?.data;
      form.setFieldsValue({
        name: data?.name,
        comments: data?.comments,
      });
    }
  }, [isEditMode, busDetails?.data, form]);

  useEffect(() => {
    const data = busDetails?.data?.result?.data;
    if (!data) return;

    let points = [];

    if (
      data.start_point?.name ||
      data.pickup_points?.length > 0 ||
      data.end_point?.name
    ) {
      if (data.start_point) {
        points.push({
          name: data.start_point.name,
          coordinates: [data.start_point.latitude, data.start_point.longitude],
          time: data.start_point.estimated_time
            ? moment(data.start_point.estimated_time, format)
            : null,
        });
      }

      if (data.pickup_points && data.pickup_points.length > 0) {
        const sortedPickups = [...data.pickup_points].sort(
          (a, b) => (a.order || 0) - (b.order || 0),
        );
        sortedPickups.forEach((p) => {
          points.push({
            name: p.name,
            coordinates: [p.latitude, p.longitude],
            time: p.estimated_time ? moment(p.estimated_time, format) : null,
          });
        });
      }

      if (data.end_point) {
        points.push({
          name: data.end_point.name,
          coordinates: [data.end_point.latitude, data.end_point.longitude],
          time: data.end_point.estimated_time
            ? moment(data.end_point.estimated_time, format)
            : null,
        });
      }
    }
    // Fallback to route_map (used by legacy routes or multi-point creation)
    else if (data.route_map && data.route_map.length > 0) {
      points = data.route_map.map((p) => ({
        name: p.name,
        coordinates: p.coordinates
          ? [p.coordinates[1], p.coordinates[0]]
          : null,
        time: p.time ? moment(p.time, format) : null,
      }));
    }
    // Deep fallback to V1 source/destination if everything else fails
    else {
      if (data.source) {
        points.push({
          name: data.source,
          coordinates: data.source_location?.coordinates
            ? [
                data.source_location.coordinates[1],
                data.source_location.coordinates[0],
              ]
            : null,
          time: null,
        });
      }
      if (data.destination) {
        points.push({
          name: data.destination,
          coordinates: data.destination_location?.coordinates
            ? [
                data.destination_location.coordinates[1],
                data.destination_location.coordinates[0],
              ]
            : null,
          time: null,
        });
      }
    }

    // Ensure at least one empty point if none found
    if (points.length === 0) {
      points.push({ name: "", coordinates: null, time: null });
    }

    setAllFields(points);
    setOtherFields(points);
  }, [isEditMode, busDetails?.data]);
  return (
    <div>
      {" "}
      <div className="pt-8">
        <div
          style={{ borderRadius: "10px", color: "#616161" }}
          className=" bg-white drop-shadow-md p-8 w-full "
        >
          <div className="flex justify-between items-center">
            <div className="text-2xl text-orange-500 font-semibold">
              Routes Management
            </div>
            <div className="text-right pt-4">
              <Button
                type="primary"
                size="large"
                onClick={() => setApplyLeavePopup(true)}
              >
                Add
              </Button>
            </div>
          </div>
          <div className="py-5">
            <Table
              rowKey="_id"
              columns={columns}
              dataSource={
                formList?.isSuccess &&
                formList?.data?.result?.data?.length > 0 &&
                formList?.data?.result?.data
                  ? formList?.data?.result?.data
                  : []
              }
              pagination={true}
              loading={formList?.isLoading}
            />
          </div>

          <Modal
            width={800}
            visible={applyLeavePopup}
            onOk={() => setApplyLeavePopup(false)}
            onCancel={() => {
              form.resetFields();
              setApplyLeavePopup(false);
              setEditData(null);
              setIsEditMode(false);
              setParentFileList([]);
              setOtherFields([{ name: "", coordinates: null, time: null }]);
              setFeeStops([]);
              setNewFeeStopName("");
              setEditingFeeStop(null);
              form.resetFields();
            }}
            footer={null}
            forceRender
          >
            <h3 className="text-lg font-bold pb-3">
              {isEditMode ? "Update Route" : " Add Route"}
            </h3>
            <div className="bg-white rounded-2xl ">
              <Form
                form={form}
                layout="vertical"
                name="basic"
                onFinish={onFinish}
                autoComplete="off"
              >
                <div className="flex gap-4">
                  <Form.Item
                    label="Name"
                    name="name"
                    style={{ width: 260, flexShrink: 0 }}
                    rules={[
                      {
                        required: true,
                        message: "Please enter Name",
                      },
                    ]}
                  >
                    <Input placeholder="Enter name" />  
                  </Form.Item>
                  {/* <Form.Item
                    label="Comment"
                    name="comments"
                    className="flex-1"
                    rules={[
                      {
                        validator: (_, value) => {
                          if (!value || value.length <= 100) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            "Comment must be at most 100 characters",
                          );
                        },
                      },
                    ]}
                  >
                    <Input
                      placeholder="Type a comment..."
                      maxLength={100}
                    />
                  </Form.Item> */}
                </div>

                <Title level={4} className="mb-4">
                  Routes Map and Schedule
                </Title>
                <LoadScript
                  googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAP_API_KEY}
                >
                  {(() => {
                    const COL_TEMPLATE =
                      "24px 65px 1fr 115px 115px 105px 32px";
                    const GAP = 12;
                    const headerStyle = {
                      display: "grid",
                      gridTemplateColumns: COL_TEMPLATE,
                      gap: GAP,
                      alignItems: "center",
                      padding: "0 13px 4px 13px",
                    };
                    const rowStyle = {
                      display: "grid",
                      gridTemplateColumns: COL_TEMPLATE,
                      gap: GAP,
                      alignItems: "center",
                      padding: "8px 12px",
                      background: "#f9fafb",
                      border: "1px solid #e5e7eb",
                      borderRadius: 8,
                    };
                    const thCls =
                      "text-xs font-medium text-gray-400 uppercase tracking-wide whitespace-nowrap";
                    // Fades the row being dragged and outlines the one it would
                    // drop onto.
                    const rowDragStyle = (index) => {
                      const isDropTarget =
                        dragIndex !== null &&
                        dropIndex === index &&
                        dragIndex !== index;
                      return {
                        ...rowStyle,
                        opacity: dragIndex === index ? 0.4 : 1,
                        background: isDropTarget
                          ? "#eff6ff"
                          : rowStyle.background,
                        border: isDropTarget
                          ? "1px dashed #2563eb"
                          : rowStyle.border,
                      };
                    };

                    return (
                      <div className="flex flex-col gap-2">
                        {/* Column headers */}
                        {otherFields.length > 0 && (
                          <div style={headerStyle}>
                            <span />
                            <span className={thCls}>Point</span>
                            <span className={thCls}>Location Name</span>
                            <span className={thCls}>Latitude</span>
                            <span className={thCls}>Longitude</span>
                            <span className={thCls}>Arrival Time</span>
                            <span />
                          </div>
                        )}

                        {/* Data rows */}
                        {otherFields?.map((field, index) => (
                          <div
                            key={index}
                            style={rowDragStyle(index)}
                            onDragOver={(e) => {
                              if (dragIndex === null) return;
                              // Without preventDefault the browser refuses the drop.
                              e.preventDefault();
                              if (dropIndex !== index) setDropIndex(index);
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              moveField(dragIndex, index);
                              setDragIndex(null);
                              setDropIndex(null);
                            }}
                          >
                            {/* Drag handle — only this starts a drag, so the
                                inputs stay selectable. */}
                            <span
                              draggable
                              onDragStart={(e) => {
                                setDragIndex(index);
                                e.dataTransfer.effectAllowed = "move";
                                e.dataTransfer.setData(
                                  "application/x-route-point",
                                  String(index),
                                );
                              }}
                              onDragEnd={() => {
                                setDragIndex(null);
                                setDropIndex(null);
                              }}
                              title="Drag to reorder"
                              className="flex justify-center text-gray-400 hover:text-gray-600"
                              style={{ cursor: "grab", userSelect: "none" }}
                            >
                              <HolderOutlined />
                            </span>

                            {/* Point badge */}
                            <span
                              className={`inline-block text-xs font-semibold px-2 py-1 rounded-full text-center ${
                                index === 0
                                  ? "bg-green-100 text-green-700"
                                  : index === otherFields.length - 1
                                    ? "bg-red-100 text-red-700"
                                    : "bg-blue-100 text-blue-700"
                              }`}
                            >
                              {index === 0
                                ? "Start"
                                : index === otherFields.length - 1
                                  ? "End"
                                  : `Stop ${index}`}
                            </span>

                            {/* Location Name */}
                            <Input
                              value={otherFields[index]?.name}
                              onChange={(e) => {
                                const value = e.target.value;
                                handleOtherFieldChange(
                                  value.charAt(0).toUpperCase() +
                                    value.slice(1),
                                  index,
                                  "name",
                                );
                              }}
                              placeholder="e.g. School Gate"
                            />

                            {/* Latitude */}
                            <Input
                              inputMode="decimal"
                              placeholder="14.387234"
                              value={otherFields[index]?.coordinates?.[0] ?? ""}
                              onChange={(e) =>
                                handleCoordinateChange(e.target.value, index, 0)
                              }
                            />

                            {/* Longitude */}
                            <Input
                              inputMode="decimal"
                              placeholder="80.142567"
                              value={otherFields[index]?.coordinates?.[1] ?? ""}
                              onChange={(e) =>
                                handleCoordinateChange(e.target.value, index, 1)
                              }
                            />

                            {/* Arrival Time */}
                            <TimePicker
                              value={
                                otherFields[index]?.time
                                  ? moment(otherFields[index]?.time, "HH:mm")
                                  : null
                              }
                              onChange={(time) =>
                                handleOtherFieldChange(time, index, "time")
                              }
                              format="HH:mm"
                              className="w-full"
                              placeholder="HH:MM"
                              minuteStep={5}
                            />

                            {/* Delete */}
                            <div className="flex justify-center">
                              <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => removeOtherField(index)}
                                tabIndex={-1}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                  <p className="text-xs text-gray-400 mt-2">
                    Tip: Right-click any spot in Google Maps → "What's here?" to
                    copy exact coordinates. Drag a row by its handle to reorder
                    the points — the first is the Start and the last is the End.
                  </p>

                  {/* Add point button */}
                  <div className="mt-4 mb-6">
                    <Button
                      type="primary"
                      htmlType="button"
                      onClick={addOtherField}
                    >
                      + Add Point
                    </Button>
                  </div>

                  {/* Route preview map */}
                  {otherFields.some(hasCoordinates) && (
                    <div className="mb-6">
                      <h6 className="text-sm font-semibold text-gray-600 mb-2">
                        Route Preview
                      </h6>
                      <GoogleMap
                        mapContainerStyle={PREVIEW_MAP_CONTAINER_STYLE}
                        onLoad={handlePreviewMapLoad}
                        options={PREVIEW_MAP_OPTIONS}
                      >
                        {otherFields.map((f, originalIndex) => {
                          if (!hasCoordinates(f)) return null;
                          const coordFields =
                            otherFields.filter(hasCoordinates);
                          const displayIndex = coordFields.findIndex(
                            (cf) => cf === f,
                          );
                          return (
                            <Marker
                              key={originalIndex}
                              position={{
                                lat: toCoordinate(f.coordinates[0]),
                                lng: toCoordinate(f.coordinates[1]),
                              }}
                              draggable={true}
                              onDragEnd={(e) =>
                                setPointCoordinates(originalIndex, [
                                  `${e.latLng.lat()}`,
                                  `${e.latLng.lng()}`,
                                ])
                              }
                              label={{
                                text:
                                  displayIndex === 0
                                    ? "S"
                                    : displayIndex === coordFields.length - 1
                                      ? "E"
                                      : `${displayIndex}`,
                                color: "white",
                              }}
                            />
                          );
                        })}
                        {previewDirections && (
                          <DirectionsRenderer
                            directions={previewDirections}
                            options={{ suppressMarkers: true }}
                          />
                        )}
                      </GoogleMap>
                      <p className="text-xs text-gray-400 mt-2">
                        Route preview follows actual road paths. Drag markers to
                        fine-tune exact positions.
                      </p>
                    </div>
                  )}
                </LoadScript>

                {isEditMode && (
                  <>
                    <Divider />
                    <Title level={4}>Fee Stops <span style={{ fontSize: 13, fontWeight: 400, color: "#888" }}>(for billing – separate from GPS tracking)</span></Title>
                    <div className="mb-3">
                      {feeStops?.map((stop, idx) => (
                        <div key={stop._id} className="flex items-center gap-2 mb-2">
                          {editingFeeStop === stop._id ? (
                            <>
                              <Input
                                value={editingFeeStopName}
                                onChange={(e) => setEditingFeeStopName(e.target.value)}
                                onPressEnter={() => handleUpdateFeeStop(stop._id)}
                                style={{ maxWidth: 300 }}
                                autoFocus
                              />
                              <Button size="small" type="primary" onClick={() => handleUpdateFeeStop(stop._id)}>Save</Button>
                              <Button size="small" onClick={() => { setEditingFeeStop(null); setEditingFeeStopName(""); }}>Cancel</Button>
                            </>
                          ) : (
                            <>
                              <span className="text-gray-700">{idx + 1}. {stop.name}</span>
                              <Button
                                size="small"
                                onClick={() => { setEditingFeeStop(stop._id); setEditingFeeStopName(stop.name); }}
                              >Edit</Button>
                              <Popconfirm
                                title="Delete this fee stop?"
                                onConfirm={() => handleDeleteFeeStop(stop._id)}
                                okText="Yes"
                                cancelText="No"
                              >
                                <Button size="small" danger>Delete</Button>
                              </Popconfirm>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center gap-2 mb-4">
                      <Input
                        placeholder="Stop name (e.g. Ramalayam Temple)"
                        value={newFeeStopName}
                        onChange={(e) => setNewFeeStopName(e.target.value)}
                        onPressEnter={handleAddFeeStop}
                        style={{ maxWidth: 300 }}
                      />
                      <Button type="dashed" onClick={handleAddFeeStop}>+ Add Fee Stop</Button>
                    </div>
                  </>
                )}
                <div className="text-center pt-2">
                  <Button
                    size="large"
                    type="primary"
                    htmlType="submit"
                    className="text-white px-10"
                  >
                    {isEditMode ? "Update" : "Submit"}
                  </Button>
                </div>
              </Form>
            </div>
          </Modal>
          <Modal
            width={800}
            visible={uploadAdmissionPopup}
            onOk={() => setUploadAdmissionPopup(false)}
            onCancel={() => {
              setUploadAdmissionPopup(false);
            }}
            footer={null}
          >
            <div>
              <MultipleUpload
                onFileListChange={handleFileListChange}
                parentResponse={parentFileList}
              />
            </div>
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default AddEditRouteLive;
